/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React from 'react';

import { Content, ContentType, Role } from 'client-code/person-content-service';

import { TIVO_SERVICES } from '../../../config';
import { DeviceModel } from '../../../core/device';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { DateTime } from '../../../core/utils';
import { ShowcaseBanner } from '../ui-kit';
import type { TInfoCardContentUtil } from './InfoCardProps';

/**
 * PersonInfoCard
 * Ref.: http://w3-engr.tivo.com/MyWorld/UserInterface/Hydra/Specs/STB/core_ux/components/components.html#person_info_card
 * There is no line limit or truncation enforced for metadata display.
 * Display all metadata available for each part, for example, title, description, or actors list.
 *
 * See redline for layout.
 *
 * Detail mode: Side caption
 *      - Part 0
 *      - Part 1
 *      - Part 2
 *      - Part 3
 *      - Part 4
 *      - Part 5
 *      - Part 6
 *      - Part 7
 *      - Detail mode: Action
 */
export const getPersonInfoCardContent: TInfoCardContentUtil = (metadata) => {
    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const contentItems: Content[] = metadata?.content || [];

    const movies = contentItems
        .filter((c) => c.contentType === ContentType.Movie && c.personRole?.includes(Role.Actor))
        .map((c) => (c.movieYear ? `${c.title} (${c.movieYear}), ` : `${c.title} `));

    const episodes = contentItems.filter(
        (c) => c.contentType === ContentType.Episode || c.contentType === ContentType.Series,
    );

    const otherCredits = contentItems.filter(
        (c) =>
            (c.contentType === ContentType.Movie ||
                c.contentType === ContentType.Series ||
                c.contentType === ContentType.Episode) &&
            !c.personRole?.includes(Role.Actor),
    );

    const formatCreditTitle = (c): string => {
        if (c.contentType === ContentType.Movie) {
            return c.movieYear ? `${c.title} (${c.movieYear})` : c.title;
        }
        return c.title;
    };
    const mappedOtherCredits = otherCredits.map(formatCreditTitle).join(', ');

    const url = assetService.getPersonImageUrl(metadata.personId || '');

    let formattedBirthday = '';
    if (metadata?.birthDate) {
        const dateTime = new DateTime(metadata.birthDate);
        formattedBirthday = dateTime.strftime('%B %d, %Y', DeviceModel.getTVProperties().getLang());
    }

    const personInfo = () => {
        return (
            <React.Fragment>
                <div className="uikit primary-text-style text-light muted-color">
                    <div className="person-info-grid">
                        <div className="person-info-row">
                            <div className="person-info-label">
                                {Locale.getString(LocaleKeys.CAST_NAME)}
                            </div>
                            <div className="person-info-value">{metadata.name || ''}</div>
                        </div>
                        <div className="person-info-row">
                            <div className="person-info-label">
                                {Locale.getString(LocaleKeys.CAST_BIRTH_DATE)}
                            </div>
                            <div className="person-info-value">{formattedBirthday || ''}</div>
                        </div>
                        <div className="person-info-row">
                            <div className="person-info-label">
                                {Locale.getString(LocaleKeys.CAST_BIRTH_PLACE)}
                            </div>
                            <div className="person-info-value">{metadata.birthPlace || ''}</div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const getBodyContent = (): JSX.Element => {
        return (
            <div className="uikit person-info">
                {metadata?.role?.length ? (
                    <div>
                        {metadata.role
                            ?.map((role) => role.charAt(0).toUpperCase() + role.slice(1))
                            .join(', ')}
                    </div>
                ) : null}
                {movies.length > 0 && (
                    <div>
                        <span className="uikit text-bold primary-color">
                            {movies.length > 1
                                ? Locale.getString(LocaleKeys.MOVIE_PLURAL)
                                : Locale.getString(LocaleKeys.MOVIE)}
                            :{' '}
                        </span>
                        {movies}
                    </div>
                )}
                {episodes.length > 0 && (
                    <div>
                        <span className="uikit text-bold primary-color">
                            {episodes.length > 1
                                ? Locale.getString(LocaleKeys.TV_SHOW_PLURAL)
                                : Locale.getString(LocaleKeys.TV_SHOW)}
                            :{' '}
                        </span>
                        {episodes.map((e) => e.title).join(', ')}
                    </div>
                )}
                {otherCredits && otherCredits.length > 0 && (
                    <div>
                        <span className="uikit text-bold primary-color">
                            {Locale.getString(LocaleKeys.OTHER_CREDITS)}:{' '}
                        </span>
                        {mappedOtherCredits}
                    </div>
                )}
            </div>
        );
    };

    return {
        headerContent: (
            <React.Fragment>
                <ShowcaseBanner bannerUrl={url} fallbackText={metadata.name || ''} />
                <div className="metadata">
                    <div className="side-captions">{personInfo()}</div>
                </div>
            </React.Fragment>
        ),
        headerClassName: 'banner-only',
        bodyContent: getBodyContent(),
    };
};
